import { useEffect, useState } from "react";
import {
    FiPlus,
    FiMinus,
    FiCreditCard,
    FiUsers,
    FiSearch,
    FiLoader,
    FiX,
} from "react-icons/fi";
import { useDebouncedCallback } from "use-debounce";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
    fetchCreditUsers,
    adjustUserCredits,
} from "../../store/smileDesign/paymentSlice";
import TablePagination from "../../components/smileDesign/TablePagination";

const ManualCredits = () => {
    const dispatch = useAppDispatch();
    const { creditUsers, totalUserCredits, creditUsersPagination, loading } =
        useAppSelector((state) => state.payment);

    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<{
        user_id: number;
        user_name: string;
        action: "add" | "remove";
    } | null>(null);
    const [credits, setCredits] = useState("");
    const [remark, setRemark] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const loadUsers = (overrides?: { page?: number; search?: string }) => {
        dispatch(
            fetchCreditUsers({
                page: overrides?.page ?? page,
                search: (overrides?.search ?? search) || undefined,
            })
        );
    };

    const debouncedSearch = useDebouncedCallback((value: string) => {
        setPage(1);
        loadUsers({ page: 1, search: value });
    }, 400);

    useEffect(() => {
        loadUsers();
    }, []);

    const handleSearch = (value: string) => {
        setSearch(value);
        debouncedSearch(value);
    };

    const openModal = (
        user_id: number,
        user_name: string,
        action: "add" | "remove"
    ) => {
        setSelectedUser({ user_id, user_name, action });
        setCredits("");
        setRemark("");
        setModalOpen(true);
    };

    const handleSubmit = async () => {
        if (!selectedUser || !credits) return;

        setSubmitting(true);
        try {
            await dispatch(
                adjustUserCredits({
                    user_id: selectedUser.user_id,
                    credits: Number(credits),
                    action: selectedUser.action,
                    remark: remark || undefined,
                })
            ).unwrap();
            setModalOpen(false);
            loadUsers({ search });
        } catch {
            // error handled in slice
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#080808] text-white p-4 sm:p-6">
            <div className="w-full">
                <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-5 mb-8">
                    <div>
                        <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                            Manual Credits
                        </h1>
                        <p className="text-[#8b8f8d] mt-2 text-sm sm:text-base">
                            Add or remove credits manually for users.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                    <div className="bg-[#101211] border border-[#1f2221] rounded-2xl p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[#8b8f8d] text-sm">Users (filtered)</p>
                                <h2 className="text-3xl font-bold text-white mt-2">
                                    {creditUsersPagination?.totalCount ?? creditUsers.length}
                                </h2>
                            </div>
                            <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400">
                                <FiUsers className="text-2xl" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-[#101211] border border-[#1f2221] rounded-2xl p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[#8b8f8d] text-sm">Total Credits</p>
                                <h2 className="text-3xl font-bold text-cyan-400 mt-2">
                                    {totalUserCredits}
                                </h2>
                            </div>
                            <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400">
                                <FiCreditCard className="text-2xl" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="relative mb-6">
                    <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6b6f6d]" />
                    <input
                        type="text"
                        placeholder="Search users..."
                        value={search}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="w-full bg-[#101211] border border-[#1f2221] rounded-2xl py-3 pl-12 pr-4 text-white placeholder:text-[#6b6f6d] focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                </div>

                {loading && (
                    <div className="flex justify-center py-20">
                        <FiLoader className="animate-spin text-cyan-400 text-4xl" />
                    </div>
                )}

                {!loading && creditUsers.length === 0 && (
                    <div className="text-center py-20 rounded-3xl border border-[#1f2221] bg-[#101211]">
                        <FiUsers className="text-5xl text-[#3a3d3c] mx-auto mb-4" />
                        <h3 className="text-xl font-semibold">No users found</h3>
                    </div>
                )}

                {!loading && creditUsers.length > 0 && (
                    <>
                    <div className="hidden lg:block bg-[#101211] border border-[#1f2221] rounded-3xl overflow-hidden shadow-2xl">
                        <table className="w-full">
                            <thead className="bg-[#080808] border-b border-[#1f2221]">
                                <tr>
                                    <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">User</th>
                                    <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">Email</th>
                                    <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">Credits</th>
                                    <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">Role</th>
                                    <th className="px-6 py-5 text-right text-[#b7bab8] font-semibold">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {creditUsers.map((user) => (
                                    <tr
                                        key={user.user_id}
                                        className="border-b border-[#1f2221] hover:bg-[#151716] transition-all"
                                    >
                                        <td className="px-6 py-5 font-semibold">{user.user_name}</td>
                                        <td className="px-6 py-5 text-[#b7bab8]">{user.email}</td>
                                        <td className="px-6 py-5">
                                            <span className="bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 px-4 py-2 rounded-full text-sm">
                                                {user.smile_credits} Credits
                                            </span>
                                        </td>
                                        <td className="px-6 py-5 text-[#8b8f8d]">{user.role}</td>
                                        <td className="px-6 py-5">
                                            <div className="flex justify-end gap-3">
                                                <button
                                                    onClick={() =>
                                                        openModal(user.user_id, user.user_name, "add")
                                                    }
                                                    className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 px-4 py-2 rounded-xl"
                                                >
                                                    <FiPlus /> Add
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        openModal(user.user_id, user.user_name, "remove")
                                                    }
                                                    className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-2 rounded-xl hover:bg-red-500 hover:text-white transition"
                                                >
                                                    <FiMinus /> Remove
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <TablePagination
                            pagination={creditUsersPagination}
                            onPageChange={(nextPage) => {
                                setPage(nextPage);
                                loadUsers({ page: nextPage });
                            }}
                            itemLabel="users"
                        />
                    </div>

                    {/* Mobile cards */}
                    <div className="lg:hidden space-y-4">
                        {creditUsers.map((user) => (
                            <div
                                key={user.user_id}
                                className="bg-[#101211] border border-[#1f2221] rounded-2xl p-4 sm:p-5"
                            >
                                <div className="min-w-0">
                                    <h3 className="font-semibold text-white truncate">
                                        {user.user_name}
                                    </h3>
                                    <p className="text-[#8b8f8d] text-sm break-all mt-1">
                                        {user.email}
                                    </p>
                                </div>
                                <div className="flex flex-wrap items-center gap-2 mt-3">
                                    <span className="bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 px-3 py-1.5 rounded-full text-sm">
                                        {user.smile_credits} Credits
                                    </span>
                                    <span className="text-[#6b6f6d] text-xs">
                                        {user.role}
                                    </span>
                                </div>
                                <div className="flex gap-2 mt-4">
                                    <button
                                        onClick={() =>
                                            openModal(user.user_id, user.user_name, "add")
                                        }
                                        className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 px-3 py-2.5 rounded-xl text-sm font-medium"
                                    >
                                        <FiPlus /> Add
                                    </button>
                                    <button
                                        onClick={() =>
                                            openModal(user.user_id, user.user_name, "remove")
                                        }
                                        className="flex-1 flex items-center justify-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400 px-3 py-2.5 rounded-xl text-sm font-medium hover:bg-red-500 hover:text-white transition"
                                    >
                                        <FiMinus /> Remove
                                    </button>
                                </div>
                            </div>
                        ))}
                        <TablePagination
                            pagination={creditUsersPagination}
                            onPageChange={(nextPage) => {
                                setPage(nextPage);
                                loadUsers({ page: nextPage });
                            }}
                            itemLabel="users"
                        />
                    </div>
                    </>
                )}
            </div>

            {modalOpen && selectedUser && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
                    <div className="w-full max-w-md rounded-3xl border border-cyan-500/20 bg-[#101211] p-6">
                        <div className="flex justify-between items-start mb-6">
                            <div>
                                <h2 className="text-xl font-bold capitalize">
                                    {selectedUser.action} Credits
                                </h2>
                                <p className="text-[#8b8f8d] text-sm mt-1">
                                    {selectedUser.user_name}
                                </p>
                            </div>
                            <button
                                onClick={() => setModalOpen(false)}
                                className="w-9 h-9 rounded-full bg-[#1a1d1c] border border-[#2a2d2c] flex items-center justify-center"
                            >
                                <FiX />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="text-sm text-[#8b8f8d]">Credits</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={credits}
                                    onChange={(e) => setCredits(e.target.value)}
                                    className="w-full mt-1 bg-[#080808] border border-[#1f2221] rounded-xl py-3 px-4 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                    placeholder="Enter amount"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-[#8b8f8d]">Remark (optional)</label>
                                <input
                                    type="text"
                                    value={remark}
                                    onChange={(e) => setRemark(e.target.value)}
                                    className="w-full mt-1 bg-[#080808] border border-[#1f2221] rounded-xl py-3 px-4 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                    placeholder="Reason for adjustment"
                                />
                            </div>
                        </div>

                        <button
                            onClick={handleSubmit}
                            disabled={submitting || !credits}
                            className="w-full mt-6 py-3.5 rounded-2xl font-semibold bg-gradient-to-r from-cyan-500 to-blue-600 disabled:opacity-50"
                        >
                            {submitting ? "Saving..." : "Confirm"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManualCredits;
